import { NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  contentChild,
  effect,
  EffectCleanupRegisterFn,
  ElementRef,
  inject,
  input,
  InputSignal,
  OnDestroy,
  output,
  OutputEmitterRef,
  Renderer2,
  signal,
  Signal,
  TemplateRef,
  viewChild,
  viewChildren,
  WritableSignal
} from '@angular/core';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { Breakpoint, BreakpointState, MediaQueryService } from '../../../services/media-query.service';
import { IconComponent } from '../../icon/icon.component';

@Component({
  selector: 'td-wb-pagination-slider',
  templateUrl: './pagination-slider.component.html',
  styleUrls: ['./pagination-slider.component.scss'],
  imports: [NgTemplateOutlet, TranslateDirective, IconComponent, TranslatePipe]
})
export class PaginationSliderComponent implements OnDestroy {
  items: InputSignal<Array<unknown>> = input<Array<unknown>>([]);
  rows: InputSignal<number> = input<number>(1);
  columns: InputSignal<number> = input<number>(1);
  showNavigation: InputSignal<boolean> = input<boolean>(true);
  enableSwipe: InputSignal<boolean> = input<boolean>(true);
  showDots: InputSignal<boolean> = input<boolean>(true);
  isGrid: InputSignal<boolean> = input<boolean>(false);

  itemClick: OutputEmitterRef<unknown> = output<unknown>();
  itemTemplate: Signal<TemplateRef<unknown> | undefined> = contentChild(TemplateRef);
  sliderContainer: Signal<ElementRef | undefined> = viewChild<ElementRef>('sliderContainer');
  allItems: Signal<ReadonlyArray<ElementRef>> = viewChildren<ElementRef>('allItems');
  currentSlide: WritableSignal<number> = signal<number>(0);
  private touchStartX: number = 0;
  private touchEndX: number = 0;
  private isDragging: boolean = false;
  private minSwipeDistance: number = 50;
  isTouched: WritableSignal<boolean> = signal<boolean>(false);
  private destroyListeners!: () => void;

  private readonly renderer = inject(Renderer2);
  private readonly mediaQueryService = inject(MediaQueryService);

  isMobile: Signal<boolean> = toSignal(
    this.mediaQueryService.observe(Breakpoint.SM).pipe(
      map((state: BreakpointState) => state.isBreakpointMatch(Breakpoint.SM))
    ),
    { initialValue: false }
  );

  constructor() {
    // effect: attaches Renderer2 touch listeners to sliderContainer — Renderer2.listen()
    // is an imperative browser API; onCleanup removes previous listeners before each re-run
    // so that sliderContainer or enableSwipe changes do not accumulate duplicate listeners.
    effect((onCleanup: EffectCleanupRegisterFn) => {
      this.setUpTouchEvents();
      onCleanup(() => this.destroyListeners?.());
    });
  }

  ngOnDestroy(): void {
    if (this.destroyListeners) {
      this.destroyListeners();
    }
  }

  slides: Signal<Array<Array<unknown>>> = computed<Array<Array<unknown>>>(() => {
    const items = this.items();
    const size = this.displayItemsPerView();
    const result: Array<Array<unknown>> = [];
    for (let i = 0; i < items?.length; i += size) {
      result.push(items?.slice(i, i + size));
    }
    return result;
  });

  translateX: Signal<number> = computed<number>(() => this.currentSlide() * 100);

  readonly sliderTrackTransform: Signal<string> = computed<string>(() => `translateX(-${this.translateX()}%)`);

  maxIndex: Signal<number> = computed<number>(() => {
    return ((this.slides()?.length ?? 1) - 1);
  });

  totalSlides: Signal<number> = computed<number>(() => Math.ceil(this.items()?.length / this.displayItemsPerView()));

  slideIndices: Signal<Array<number>> = computed<Array<number>>(() => Array.from({ length: this.totalSlides() }, (_v: unknown, i: number): number => i));

  canGoNext: Signal<boolean> = computed<boolean>(() => this.currentSlide() < this.totalSlides() - 1);

  canGoPrev: Signal<boolean> = computed<boolean>(() => this.currentSlide() > 0);

  displayItemsPerView: Signal<number> = computed<number>(() => this.isMobile() ? 1 : this.rows() * this.columns());

  displayDots: Signal<boolean> = computed<boolean>(() => this.isMobile() ? false : this.showDots());

  next(): false {
    this.currentSlide.update((index: number) => Math.min(index + 1, this.maxIndex()));
    return false; // Prevent default action
  }

  prev(): false {
    this.currentSlide.update((index: number) => Math.max(index - 1, 0));
    return false; // Prevent default action
  }

  goToSlide(slideIndex: number): false {
    this.currentSlide.set(slideIndex);
    return false; // Prevent default action
  }

  onItemClick<T>(item: T): void {
    if (this.isTouched() || !this.isDragging) {
      this.isTouched.set(false); // Reset click state after handling
      this.itemClick.emit(item);
    }
  }

  // Touch event handlers
  onDragStart(event: TouchEvent): void {
    if (!this.enableSwipe()) return;
    if (this.isButton(event)) {
      this.isDragging = false; // If a button is pressed, do not allow dragging
      return;
    }
    this.touchStartX = this.getClientX(event);
    this.isDragging = true;
    this.isTouched.set(true); // Assume it's a click first and check later.
  }

  onDragMove(event: TouchEvent): void {
    if (!this.enableSwipe() || !this.isDragging) return;
    this.touchEndX = this.getClientX(event);
    const deltaX = Math.abs(this.touchEndX - this.touchStartX);
    if (deltaX > 5) {
      this.isTouched.set(false); // This is a swipe, not a click
    }
  }

  onDragEnd(event: TouchEvent): void {
    if (!this.enableSwipe() || !this.isDragging) return;
    const swipeDistance = this.touchEndX - this.touchStartX;
    const absSwipeDistance = Math.abs(swipeDistance);
    // Only consider it a swipe if:
    // 1. Distance is sufficient
    if (!this.isTouched() && absSwipeDistance >= this.minSwipeDistance) {
      if (swipeDistance > 0) {
        this.prev();
      } else {
        this.next();
      }
      // Prevent click event from firing after swipe
    } else if (this.isTouched()) {
      this.handleItemClick(event);
    }
    this.isTouched.set(false);
    this.isDragging = false; // Reset dragging state
  }

  private handleItemClick(event: TouchEvent): void {
    const target = event.target as HTMLElement;
    const itemElement = target.closest('.td-wb-pagination-slider-item');
    if (itemElement) {
      const allItemsArray = this.allItems();
      const clickedItem = allItemsArray.findIndex((item: ElementRef) => item.nativeElement === itemElement);
      if (clickedItem !== -1) {
        const clickedItemData = this.items()[clickedItem];
        this.onItemClick(clickedItemData);
      }
    }
  }

  private getClientX(event: TouchEvent): number {
    return event.touches[0].clientX;
  }

  private setUpTouchEvents(): void {
    const container = this.sliderContainer();
    if (!container?.nativeElement || !this.enableSwipe()) return;
    const containerEl = container.nativeElement;
    const touchStartListener = this.renderer.listen(containerEl, 'touchstart', (event: TouchEvent) => this.onDragStart(event), { passive: false });
    const touchMoveListener = this.renderer.listen(containerEl, 'touchmove', (event: TouchEvent) => this.onDragMove(event), { passive: false });
    const touchEndListener = this.renderer.listen(containerEl, 'touchend', (event: TouchEvent) => this.onDragEnd(event), { passive: false });
    this.destroyListeners = (): void => { touchStartListener(); touchMoveListener(); touchEndListener(); };
  }

  private isButton(event: TouchEvent): boolean {
    const target = event.target as HTMLElement;
    return !!target.closest('button');
  }
}